import type { DOMOutputSpec, Node, NodeSpec } from 'prosemirror-model';

import { AlignExtension } from '../../../extensions/align';
import { HtmlClassExtension } from '../../../extensions/html-class';
import { NodeName } from '../../../schema/nodes';
import { DEFAULT_VIDEO_WIDTH, DEFAULT_VIDEO_HEIGHT } from '../video-plugin.consts';

export class VideoNode implements NodeSpec {
  public static readonly nodeName = NodeName.Video;
  public static is(node: Node) {
    return node.type.name === VideoNode.nodeName;
  }

  public readonly alignExtension?: AlignExtension;
  public readonly htmlClassExtension?: HtmlClassExtension;

  public attrs = {
    src: { default: '' },
    width: { default: DEFAULT_VIDEO_WIDTH },
    height: { default: DEFAULT_VIDEO_HEIGHT },
    ...(this.config?.withAlign && AlignExtension.attrs),
    ...(this.config?.withHtmlClass && HtmlClassExtension.attrs),
  };
  public inline = false;
  public group = 'block';

  public parseDOM = [
    {
      tag: 'figure',
      priority: 51,
      getAttrs: (dom: HTMLElement) => {
        const iframeEl = dom.querySelector('iframe');
        if (!iframeEl) {
          return false;
        }

        const align = dom.style.textAlign;
        const html_class = dom.getAttribute('class');
        const width = Number(iframeEl.getAttribute('width')) || DEFAULT_VIDEO_WIDTH;
        const height = Number(iframeEl.getAttribute('height')) || DEFAULT_VIDEO_HEIGHT;
        return {
          src: iframeEl.getAttribute('src') || '',
          width,
          height,
          align,
          'html-class': html_class,
        };
      },
    },
  ];

  constructor(private config?: { withAlign?: boolean; withHtmlClass?: boolean }) {
    if (config?.withAlign) this.alignExtension = new AlignExtension();
    if (config?.withHtmlClass) this.htmlClassExtension = new HtmlClassExtension();
  }

  public toDOM = (node: Node): DOMOutputSpec => {
    const { src, width, height } = <{ src: string; width: number; height: number }>node.attrs;
    const alignStyle = this.alignExtension?.getStyleSerialization(node);
    const htmlClass = this.htmlClassExtension?.getClassSerialization(node);
    const figureEl = <[string, ...any]>[
      'figure',
      {
        ...(alignStyle && {
          style: alignStyle,
        }),
        ...(htmlClass && {
          class: htmlClass,
        }),
      },
    ];

    const iframeEl = <[string, ...any]>[
      'iframe',
      {
        src: src,
        width: width || DEFAULT_VIDEO_WIDTH,
        height: height || DEFAULT_VIDEO_HEIGHT,
      },
    ];

    figureEl.push(iframeEl);

    return figureEl;
  };
}
